import React, { useEffect, useState } from 'react';
import { planService } from '../services/api';

const VALUE_TYPES = ['BOOLEAN', 'INTEGER', 'FLOAT', 'JSON'];

const EMPTY_FORM = {
  key: '',
  name: '',
  description: '',
  valueType: 'BOOLEAN',
  category: '',
  isAddon: false,
  requiredForPublished: false,
  validationSchema: '',
  uiSchema: '',
  translations: {
    it: { name: '', description: '' },
    en: { name: '', description: '' },
  },
};

function getTranslationsByLocale(translations = []) {
  return Object.fromEntries(translations.map((translation) => [translation.locale, translation]));
}

function isValidJson(str) {
  if (!str.trim()) return true;
  try { JSON.parse(str); return true; } catch { return false; }
}

export default function PlanFeatures() {
  const [defs, setDefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // modal state
  const [modal, setModal] = useState(null); // null | { mode: 'create' | 'edit', def?: {} }
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  // delete confirm
  const [deleteTarget, setDeleteTarget] = useState(null); // def object
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  // expand advanced fields
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [newCategoryMode, setNewCategoryMode] = useState(false);

  // public pricing order
  const [showReorder, setShowReorder] = useState(false);
  const [reorderItems, setReorderItems] = useState([]);
  const [savingReorder, setSavingReorder] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await planService.listFeatureDefinitions();
      setDefs(res?.data || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setForm({
      ...EMPTY_FORM,
      translations: {
        it: { ...EMPTY_FORM.translations.it },
        en: { ...EMPTY_FORM.translations.en },
      },
    });
    setFormError(null);
    setShowAdvanced(false);
    setNewCategoryMode(false);
    setModal({ mode: 'create' });
  };

  const openEdit = (def) => {
    const translations = getTranslationsByLocale(def.translations);
    setForm({
      key: def.key,
      name: def.name,
      description: def.description || '',
      valueType: def.valueType,
      category: def.category,
      isAddon: !!def.isAddon,
      requiredForPublished: !!def.requiredForPublished,
      validationSchema: def.validationSchema ? JSON.stringify(def.validationSchema, null, 2) : '',
      uiSchema: def.uiSchema ? JSON.stringify(def.uiSchema, null, 2) : '',
      translations: {
        it: {
          name: translations.it?.name || '',
          description: translations.it?.description || '',
        },
        en: {
          name: translations.en?.name || '',
          description: translations.en?.description || '',
        },
      },
    });
    setFormError(null);
    setShowAdvanced(!!(def.validationSchema || def.uiSchema));
    // if category exists in list use dropdown, otherwise force new-category mode
    const existingCats = [...new Set(defs.map((d) => d.category).filter(Boolean))];
    setNewCategoryMode(!existingCats.includes(def.category));
    setModal({ mode: 'edit', def });
  };

  const closeModal = () => {
    setModal(null);
    setFormError(null);
    setNewCategoryMode(false);
  };

  const handleSave = async () => {
    setFormError(null);
    if (!form.key.trim()) { setFormError('Il codice (key) è obbligatorio'); return; }
    if (!form.name.trim()) { setFormError('Il nome è obbligatorio'); return; }
    if (!form.category.trim()) { setFormError('La categoria è obbligatoria'); return; }
    for (const locale of ['it', 'en']) {
      if (!form.translations[locale].name.trim()) { setFormError(`Il nome ${locale.toUpperCase()} è obbligatorio`); return; }
      if (!form.translations[locale].description.trim()) { setFormError(`La descrizione ${locale.toUpperCase()} è obbligatoria`); return; }
    }
    if (!isValidJson(form.validationSchema)) { setFormError('validationSchema non è JSON valido'); return; }
    if (!isValidJson(form.uiSchema)) { setFormError('uiSchema non è JSON valido'); return; }

    const payload = {
      key: form.key.trim(),
      name: form.name.trim(),
      description: form.description.trim() || null,
      valueType: form.valueType,
      category: form.category.trim(),
      isAddon: form.isAddon,
      requiredForPublished: form.requiredForPublished,
      validationSchema: form.validationSchema.trim() ? JSON.parse(form.validationSchema) : null,
      uiSchema: form.uiSchema.trim() ? JSON.parse(form.uiSchema) : null,
      translations: {
        it: {
          name: form.translations.it.name.trim(),
          description: form.translations.it.description.trim(),
        },
        en: {
          name: form.translations.en.name.trim(),
          description: form.translations.en.description.trim(),
        },
      },
    };

    try {
      setSaving(true);
      if (modal.mode === 'create') {
        await planService.createFeatureDefinition(payload);
      } else {
        await planService.updateFeatureDefinition(modal.def.id, payload);
      }
      closeModal();
      await load();
    } catch (err) {
      setFormError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  const openDelete = (def) => {
    setDeleteTarget(def);
    setDeleteConfirmText('');
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await planService.deleteFeatureDefinition(deleteTarget.id);
      setDeleteTarget(null);
      await load();
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    } finally {
      setDeleting(false);
    }
  };

  const openReorderModal = () => {
    setReorderItems(defs.map((def, index) => ({
      featureDefinitionId: def.id,
      key: def.key,
      name: getTranslationsByLocale(def.translations).it?.name || def.name,
      category: def.category,
      displayOrder: index,
    })));
    setShowReorder(true);
  };

  const moveReorderItem = (index, direction) => {
    setReorderItems((items) => {
      const next = [...items];
      const swapIndex = index + direction;
      if (swapIndex < 0 || swapIndex >= next.length) return items;
      [next[index], next[swapIndex]] = [next[swapIndex], next[index]];
      return next.map((item, itemIndex) => ({ ...item, displayOrder: itemIndex }));
    });
  };

  const saveReorder = async () => {
    try {
      setSavingReorder(true);
      await planService.reorderFeatureDefinitions(reorderItems.map(({ featureDefinitionId, displayOrder }) => ({
        featureDefinitionId,
        displayOrder,
      })));
      await load();
      setShowReorder(false);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSavingReorder(false);
    }
  };

  // group definitions by category for display
  const grouped = defs.reduce((acc, d) => {
    const cat = d.category || 'Senza categoria';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(d);
    return acc;
  }, {});

  const isEditingKey = modal?.mode === 'edit' && form.key !== modal?.def?.key;

  return (
    <div className="page-content">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div>
          <h1 className="page-title">Plan Features</h1>
          <p className="page-subtitle" style={{ margin: '4px 0 0', color: '#64748b', fontSize: 14 }}>
            Catalogo globale delle feature definizioni. Ogni feature può essere associata a versioni di piano.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn" onClick={openReorderModal} disabled={defs.length < 2}>↕ Ordina feature</button>
          <button className="btn btn-primary" onClick={openCreate}>+ Nuova feature</button>
        </div>
      </div>

      {showReorder && (
        <div className="modal-overlay" onClick={() => setShowReorder(false)}>
          <div className="modal" style={{ maxWidth: 520, width: '100%' }} onClick={(event) => event.stopPropagation()}>
            <div className="modal-header">
              <h3 style={{ margin: 0 }}>Ordine feature nella pricing page</h3>
              <button className="modal-close" onClick={() => setShowReorder(false)}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ margin: '0 0 16px', fontSize: 13, color: '#64748b' }}>
                Usa le frecce per definire l’ordine delle feature nelle card e nella tabella comparativa pubblica.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {reorderItems.map((item, index) => (
                  <div key={item.featureDefinitionId} style={{ display: 'flex', alignItems: 'center', gap: 10, border: '1px solid #e2e8f0', borderRadius: 6, padding: '8px 10px' }}>
                    <span style={{ width: 22, textAlign: 'center', color: '#94a3b8', fontWeight: 700 }}>{index + 1}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600 }}>{item.name}</div>
                      <div style={{ marginTop: 2, fontSize: 11, color: '#94a3b8' }}>{item.key} · {item.category}</div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <button className="btn icon-btn" onClick={() => moveReorderItem(index, -1)} disabled={index === 0} title="Sposta su">▲</button>
                      <button className="btn icon-btn" onClick={() => moveReorderItem(index, 1)} disabled={index === reorderItems.length - 1} title="Sposta giù">▼</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn" onClick={() => setShowReorder(false)} disabled={savingReorder}>Annulla</button>
              <button className="btn btn-primary" onClick={saveReorder} disabled={savingReorder}>
                {savingReorder ? 'Salvataggio...' : 'Salva ordine'}
              </button>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>
      )}

      {loading ? (
        <div className="card"><p>Caricamento...</p></div>
      ) : defs.length === 0 ? (
        <div className="card"><p style={{ color: '#64748b' }}>Nessuna feature definita.</p></div>
      ) : (
        Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).map(([cat, items]) => (
          <div key={cat} className="card" style={{ marginBottom: 16 }}>
            <div style={{ padding: '12px 16px 8px', borderBottom: '1px solid #f1f5f9' }}>
              <span style={{
                background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6,
                padding: '3px 10px', fontSize: 12, fontWeight: 700, color: '#475569', letterSpacing: 0.5,
                textTransform: 'uppercase',
              }}>
                {cat}
              </span>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  <th style={th}>Codice (key)</th>
                  <th style={th}>Traduzioni</th>
                  <th style={th}>Tipo</th>
                  <th style={th}>Descrizione</th>
                  <th style={th}>Usata in</th>
                  <th style={th}></th>
                </tr>
              </thead>
              <tbody>
                {items.map((def) => {
                  const usageCount = def._count?.planFeatures ?? 0;
                  const translations = getTranslationsByLocale(def.translations);
                  return (
                    <tr key={def.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={td}>
                        <code style={{ background: '#f1f5f9', borderRadius: 4, padding: '2px 6px', fontSize: 12, color: '#0f172a' }}>
                          {def.key}
                        </code>
                        {def.requiredForPublished && (
                          <span style={{ display: 'block', marginTop: 5, background: '#fef3c7', color: '#92400e', fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 999, width: 'fit-content' }}>
                            REQUIRED
                          </span>
                        )}
                        {def.isAddon && (
                          <span style={{ display: 'block', marginTop: 5, background: '#ecfeff', color: '#0e7490', fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 999, width: 'fit-content' }}>
                            ADD-ON
                          </span>
                        )}
                      </td>
                      <td style={td}>
                        <div style={{ fontWeight: 600 }}>IT: {translations.it?.name || <span style={{ color: '#ef4444' }}>mancante</span>}</div>
                        <div style={{ marginTop: 3, color: '#64748b' }}>EN: {translations.en?.name || <span style={{ color: '#ef4444' }}>missing</span>}</div>
                      </td>
                      <td style={td}>
                        <ValueTypeBadge type={def.valueType} />
                      </td>
                      <td style={{ ...td, color: '#64748b', fontSize: 13, maxWidth: 260 }}>
                        {def.description || <span style={{ color: '#cbd5e1' }}>—</span>}
                      </td>
                      <td style={td}>
                        <span style={{
                          background: usageCount > 0 ? '#eff6ff' : '#f8fafc',
                          color: usageCount > 0 ? '#1d4ed8' : '#94a3b8',
                          fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 999,
                        }}>
                          {usageCount} {usageCount === 1 ? 'versione' : 'versioni'}
                        </span>
                      </td>
                      <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <button className="btn icon-btn" title="Modifica" onClick={() => openEdit(def)} style={{ marginRight: 4 }}>
                          ✎
                        </button>
                        <button
                          className="btn btn-danger icon-btn"
                          title={usageCount > 0 ? `In uso in ${usageCount} versione/i — rimuovila prima dai piani` : 'Elimina'}
                          onClick={() => usageCount === 0 ? openDelete(def) : alert(`Impossibile eliminare: questa feature è usata in ${usageCount} versione/i di piano. Rimuovila prima da tutti i piani.`)}
                        >
                          ✕
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))
      )}

      {/* Create / Edit modal */}
      {modal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" style={{ maxWidth: 560, width: '100%' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 style={{ margin: 0 }}>
                {modal.mode === 'create' ? 'Nuova feature' : `Modifica feature — ${modal.def.key}`}
              </h3>
              <button className="modal-close" onClick={closeModal}>×</button>
            </div>

            {modal.mode === 'create' && (
              <div style={{
                margin: '0 24px', padding: '10px 14px', background: '#fffbeb',
                border: '1px solid #fcd34d', borderRadius: 8, fontSize: 13, color: '#78350f',
              }}>
                <strong>⚠️ Nota:</strong> Creare una feature qui la inserisce nel catalogo, ma{' '}
                <strong>non la rende automaticamente funzionante nella piattaforma</strong>.
                Perché la feature venga effettivamente usata deve essere implementata nel codice backend e frontend.
              </div>
            )}

            <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={labelStyle}>
                  Codice (key) <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  className="form-input"
                  value={form.key}
                  onChange={(e) => setForm((f) => ({ ...f, key: e.target.value.toUpperCase().replace(/\s/g, '_') }))}
                  placeholder="es. MAX_WORKERS"
                  style={{ fontFamily: 'monospace' }}
                />
                {modal.mode === 'edit' && isEditingKey && (
                  <div style={{ marginTop: 6, padding: '7px 10px', background: '#fff1f2', border: '1px solid #fecdd3', borderRadius: 6, fontSize: 12, color: '#be123c' }}>
                    ⚠️ Stai modificando il codice identificativo. Assicurati che nel codice backend e frontend non ci siano riferimenti all&apos;vecchio key <code>{modal.def.key}</code>, altrimenti la feature smetterà di funzionare.
                  </div>
                )}
              </div>

              <div>
                <label style={labelStyle}>Nome tecnico / fallback <span style={{ color: '#ef4444' }}>*</span></label>
                <input
                  className="form-input"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="es. Maximum Workers"
                />
              </div>

              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Categoria <span style={{ color: '#ef4444' }}>*</span></label>
                  {(() => {
                    const existingCats = [...new Set(defs.map((d) => d.category).filter(Boolean))].sort();
                    return (
                      <>
                        {!newCategoryMode ? (
                          <select
                            className="form-input"
                            value={form.category}
                            onChange={(e) => {
                              if (e.target.value === '__new__') {
                                setNewCategoryMode(true);
                                setForm((f) => ({ ...f, category: '' }));
                              } else {
                                setForm((f) => ({ ...f, category: e.target.value }));
                              }
                            }}
                          >
                            <option value="">— Seleziona categoria —</option>
                            {existingCats.map((c) => <option key={c} value={c}>{c}</option>)}
                            <option value="__new__">+ Aggiungi nuova…</option>
                          </select>
                        ) : (
                          <div style={{ display: 'flex', gap: 6 }}>
                            <input
                              className="form-input"
                              value={form.category}
                              onChange={(e) => setForm((f) => ({ ...f, category: e.target.value.toUpperCase() }))}
                              placeholder="NUOVA CATEGORIA"
                              style={{ flex: 1 }}
                              autoFocus
                            />
                            {existingCats.length > 0 && (
                              <button
                                type="button"
                                className="btn"
                                style={{ whiteSpace: 'nowrap', fontSize: 12 }}
                                onClick={() => { setNewCategoryMode(false); setForm((f) => ({ ...f, category: '' })); }}
                              >
                                ← Lista
                              </button>
                            )}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Tipo di valore <span style={{ color: '#ef4444' }}>*</span></label>
                  <select
                    className="form-input"
                    value={form.valueType}
                    onChange={(e) => setForm((f) => ({ ...f, valueType: e.target.value }))}
                  >
                    {VALUE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label style={labelStyle}>Descrizione tecnica / fallback</label>
                <textarea
                  className="form-input"
                  rows={2}
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  placeholder="Descrizione interna usata come fallback"
                  style={{ resize: 'vertical' }}
                />
              </div>

              <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: 14 }}>
                <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={form.isAddon}
                    onChange={(e) => setForm((f) => ({ ...f, isAddon: e.target.checked }))}
                  />
                  Disponibile come Add-on
                </label>
                <div style={{ marginTop: 5, marginLeft: 21, fontSize: 12, color: '#64748b' }}>
                  Mostra il badge Add-on accanto alla feature nella pricing page.
                </div>
              </div>

              <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: 14 }}>
                <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={form.requiredForPublished}
                    onChange={(e) => setForm((f) => ({ ...f, requiredForPublished: e.target.checked }))}
                  />
                  Obbligatoria per pubblicare una versione
                </label>
              </div>

              <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: 14 }}>
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>Traduzioni per l&apos;utente finale</div>
                  <div style={{ marginTop: 3, fontSize: 12, color: '#64748b' }}>
                    Usa nomi brevi e descrizioni comprensibili, senza riferimenti ai codici tecnici.
                  </div>
                </div>
                {[
                  { locale: 'it', label: 'Italiano' },
                  { locale: 'en', label: 'English' },
                ].map(({ locale, label }) => (
                  <div key={locale} style={{ marginBottom: locale === 'it' ? 16 : 0 }}>
                    <div style={{ marginBottom: 8, fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase' }}>
                      {label}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <div>
                        <label style={labelStyle}>Nome <span style={{ color: '#ef4444' }}>*</span></label>
                        <input
                          className="form-input"
                          value={form.translations[locale].name}
                          onChange={(event) => setForm((current) => ({
                            ...current,
                            translations: {
                              ...current.translations,
                              [locale]: { ...current.translations[locale], name: event.target.value },
                            },
                          }))}
                          placeholder={locale === 'it' ? 'es. Gestione dei turni' : 'e.g. Shift management'}
                        />
                      </div>
                      <div>
                        <label style={labelStyle}>Descrizione <span style={{ color: '#ef4444' }}>*</span></label>
                        <textarea
                          className="form-input"
                          rows={2}
                          value={form.translations[locale].description}
                          onChange={(event) => setForm((current) => ({
                            ...current,
                            translations: {
                              ...current.translations,
                              [locale]: { ...current.translations[locale], description: event.target.value },
                            },
                          }))}
                          placeholder={locale === 'it' ? 'Descrivi il vantaggio per il cliente.' : 'Describe the customer benefit.'}
                          style={{ resize: 'vertical' }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Advanced JSON fields */}
              <div>
                <button
                  type="button"
                  style={{ background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', fontSize: 13, padding: 0 }}
                  onClick={() => setShowAdvanced((v) => !v)}
                >
                  {showAdvanced ? '▾' : '▸'} Campi avanzati (validationSchema, uiSchema)
                </button>

                {showAdvanced && (
                  <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div>
                      <label style={labelStyle}>validationSchema <span style={{ color: '#94a3b8', fontWeight: 400 }}>(JSON)</span></label>
                      <textarea
                        className="form-input"
                        rows={4}
                        value={form.validationSchema}
                        onChange={(e) => setForm((f) => ({ ...f, validationSchema: e.target.value }))}
                        placeholder={'{\n  "minimum": 0,\n  "maximum": 100\n}'}
                        style={{ fontFamily: 'monospace', fontSize: 12, resize: 'vertical' }}
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>uiSchema <span style={{ color: '#94a3b8', fontWeight: 400 }}>(JSON)</span></label>
                      <textarea
                        className="form-input"
                        rows={4}
                        value={form.uiSchema}
                        onChange={(e) => setForm((f) => ({ ...f, uiSchema: e.target.value }))}
                        placeholder={'{\n  "widget": "range"\n}'}
                        style={{ fontFamily: 'monospace', fontSize: 12, resize: 'vertical' }}
                      />
                    </div>
                  </div>
                )}
              </div>

              {formError && (
                <div style={{ padding: '8px 12px', background: '#fff1f2', border: '1px solid #fecdd3', borderRadius: 6, color: '#be123c', fontSize: 13 }}>
                  {formError}
                </div>
              )}
            </div>

            <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn" onClick={closeModal}>Annulla</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Salvataggio...' : modal.mode === 'create' ? 'Crea feature' : 'Salva modifiche'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {deleteTarget && (
        <div className="modal-overlay" onClick={() => setDeleteTarget(null)}>
          <div className="modal" style={{ maxWidth: 460, width: '100%' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 style={{ margin: 0, color: '#be123c' }}>Elimina feature</h3>
              <button className="modal-close" onClick={() => setDeleteTarget(null)}>×</button>
            </div>

            <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <p style={{ margin: 0, fontSize: 14 }}>
                Stai per eliminare definitivamente la feature{' '}
                <strong>
                  <code style={{ background: '#f1f5f9', padding: '1px 6px', borderRadius: 4 }}>{deleteTarget.key}</code>
                </strong>
                {' '}— <em>{deleteTarget.name}</em>.
              </p>
              <p style={{ margin: 0, fontSize: 13, color: '#64748b' }}>
                Questa operazione è irreversibile. Per confermare, digita il codice della feature nel campo sottostante.
              </p>
              <div>
                <label style={labelStyle}>
                  Digita <code style={{ background: '#f1f5f9', padding: '1px 5px', borderRadius: 3, fontSize: 12 }}>{deleteTarget.key}</code> per confermare
                </label>
                <input
                  className="form-input"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder={deleteTarget.key}
                  style={{ fontFamily: 'monospace' }}
                />
              </div>
            </div>

            <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn" onClick={() => setDeleteTarget(null)}>Annulla</button>
              <button
                className="btn btn-danger"
                disabled={deleteConfirmText !== deleteTarget.key || deleting}
                onClick={handleDelete}
              >
                {deleting ? 'Eliminazione...' : 'Elimina definitivamente'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ValueTypeBadge({ type }) {
  const colors = {
    BOOLEAN: { bg: '#f0fdf4', color: '#15803d' },
    INTEGER: { bg: '#eff6ff', color: '#1d4ed8' },
    FLOAT: { bg: '#eff6ff', color: '#1d4ed8' },
    JSON: { bg: '#fdf4ff', color: '#7e22ce' },
  };
  const s = colors[type] || { bg: '#f8fafc', color: '#475569' };
  return (
    <span style={{ background: s.bg, color: s.color, fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999 }}>
      {type}
    </span>
  );
}

const th = { padding: '8px 12px', textAlign: 'left', fontWeight: 600, fontSize: 12, color: '#64748b', borderBottom: '1px solid #e2e8f0' };
const td = { padding: '10px 12px', fontSize: 13, verticalAlign: 'middle' };
const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 };
